import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';
import { iniciaisDe } from '../montagem';
import SeletorAlocar from './SeletorAlocar';
import { calcularSugestoes } from '../sugestaoEquipe';
import type {
  EquipePlacar,
  FichaInscrito,
  PessoaMontagem,
  RespostaFicha,
} from '../types';

interface Props {
  pessoa: PessoaMontagem;
  fotoUrl: string | null;
  equipes: EquipePlacar[];
  aoFechar: () => void;
  aoEscolherEquipe: (p: PessoaMontagem, equipeId: number) => void;
  aoDesalocar: (p: PessoaMontagem) => void;
  aoToast: (msg: string) => void;
}

type Estado = 'carregando' | 'pronto' | 'erro';

// Converte o valor da resposta para texto de exibição.
function valoresDe(r: RespostaFicha): string[] {
  if (Array.isArray(r.resposta)) {
    return r.resposta.filter(Boolean).map((v) => String(v));
  }
  if (r.resposta === null || r.resposta === undefined) return [];
  if (typeof r.resposta === 'boolean') return [r.resposta ? 'Sim' : 'Não'];
  return [String(r.resposta)];
}

function ehObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

// Pergunta tipo lista (ex.: "Quem são eles?"): um bloco por linha, com
// "Rótulo: valor" em linhas. Nada de tabela larga de 8 colunas.
// A resposta de uma lista pode chegar como array ou como texto JSON.
function linhasDaLista(valor: unknown): unknown[] {
  if (Array.isArray(valor)) return valor;
  if (typeof valor === 'string' && valor.trim()) {
    try {
      const lido = JSON.parse(valor);
      return Array.isArray(lido) ? lido : [];
    } catch {
      return [];
    }
  }
  return [];
}

function ListaResposta({ r }: { r: RespostaFicha }) {
  const linhas = linhasDaLista(r.resposta);
  if (!r.respondeu) {
    return <p className="mt-1 text-sm italic text-secondary-300">não respondeu</p>;
  }
  if (linhas.length === 0) {
    const texto = typeof r.resposta === 'string' ? r.resposta.trim() : '';
    if (!texto) return <p className="mt-1 text-sm text-secondary-300">—</p>;
    return <p className="mt-1 whitespace-pre-line text-sm text-foreground-800">{texto}</p>;
  }
  return (
    <div className="mt-1.5 space-y-2">
      {linhas.map((linha, indice) => {
        const itens = ehObjeto(linha)
          ? Object.entries(linha).filter(([, v]) => String(v ?? '').trim() !== '')
          : [];
        return (
          <div key={indice} className="rounded-md bg-background-100 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">
              {indice + 1}
            </p>
            {itens.length === 0 ? (
              <p className="mt-0.5 text-sm text-secondary-400">—</p>
            ) : (
              itens.map(([rotulo, valor]) => (
                <p key={rotulo} className="mt-0.5 text-[13px] leading-relaxed text-foreground-800">
                  <span className="font-semibold text-foreground-900">{rotulo}: </span>
                  <span className="whitespace-pre-line">{String(valor)}</span>
                </p>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

// Resposta longa (tipo texto_longo ou lista) vai para baixo da pergunta.
function ehBloco(r: RespostaFicha): boolean {
  if (r.tipo === 'texto_longo' || r.tipo === 'lista' || r.tipo === 'multipla_escolha') {
    return true;
  }
  return valoresDe(r).join(' ').length > 70;
}

// Quais respostas importam para montar equipe (talentos, já serviu,
// disponibilidade, restrições, filhos pequenos). Prefere a chave semântica;
// cai para o texto da pergunta quando não há chave.
const PESAM_REGEX = [/talento/i, /serviu/i, /disponib/i, /restri/i, /filho/i, /pastoreio/i];

function pesaNaEscolha(r: RespostaFicha): boolean {
  const alvo = `${r.chave ?? ''} ${r.pergunta}`;
  return PESAM_REGEX.some((re) => re.test(alvo));
}

function agruparPorSecao(respostas: RespostaFicha[]): { secao: string | null; itens: RespostaFicha[] }[] {
  const ordem: (string | null)[] = [];
  const mapa = new Map<string | null, RespostaFicha[]>();
  for (const r of respostas) {
    if (!mapa.has(r.secao)) {
      mapa.set(r.secao, []);
      ordem.push(r.secao);
    }
    mapa.get(r.secao)!.push(r);
  }
  return ordem.map((s) => ({ secao: s, itens: mapa.get(s)! }));
}

export default function PainelFicha({
  pessoa,
  fotoUrl,
  equipes,
  aoFechar,
  aoEscolherEquipe,
  aoDesalocar,
  aoToast,
}: Props) {
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [ficha, setFicha] = useState<FichaInscrito | null>(null);
  const [fotos, setFotos] = useState<Record<string, string>>({});
  const [recarregar, setRecarregar] = useState(0);
  const [mostrarExplicacao, setMostrarExplicacao] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function buscar() {
      setEstado('carregando');
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('ficha_do_inscrito', {
          p_inscricao: pessoa.inscricao_id,
        });
        if (error) throw error;

        const f = (Array.isArray(data) ? data[0] : data) as FichaInscrito | null;
        if (!f || f.ok === false) {
          aoToast(f?.erro || 'Você não tem acesso a esta ficha.');
          aoFechar();
          return;
        }

        // Resolve as URLs assinadas das respostas tipo "foto" (um lote só).
        const caminhos = Array.from(
          new Set(
            (f.respostas ?? [])
              .filter(
                (r) =>
                  r.tipo === 'foto' &&
                  typeof r.resposta === 'string' &&
                  r.resposta &&
                  !r.resposta.startsWith('http'),
              )
              .map((r) => r.resposta as string),
          ),
        );
        let novos: Record<string, string> = {};
        if (caminhos.length > 0) {
          const { data: sd, error: se } = await supabase.storage
            .from('fichas')
            .createSignedUrls(caminhos, 3600);
          if (!se && sd) {
            (sd as { path?: string; signedUrl?: string }[]).forEach((it) => {
              if (it?.signedUrl && it.path) novos[it.path] = it.signedUrl;
            });
          }
        }

        if (!ativo) return;
        setFicha(f);
        setFotos(novos);
        setEstado('pronto');
      } catch (e) {
        if (!ativo) return;
        setErro(mensagemBanco(e));
        setEstado('erro');
      }
    }
    buscar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pessoa.inscricao_id, recarregar]);

  // Esc fecha o painel; trava o scroll do fundo enquanto aberto.
  useEffect(() => {
    function aoTecla(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (mostrarExplicacao) setMostrarExplicacao(false);
        else aoFechar();
      }
    }
    document.addEventListener('keydown', aoTecla);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', aoTecla);
      document.body.style.overflow = original;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarExplicacao, aoFechar]);

  const participacao = ficha?.inscricao?.participacao || '';
  const equipeAtual = pessoa.equipe_atual;
  const linhaPapel = [participacao, equipeAtual || 'sem equipe'].filter(Boolean).join(' · ');

  const idade = ficha?.pessoa?.idade != null ? `${ficha.pessoa.idade} anos` : '';
  const cidade = ficha?.pessoa?.cidade || '';
  const uf = ficha?.pessoa?.uf || '';
  const local = [cidade, uf].filter(Boolean).join('/');
  const subtitulo = [idade, local].filter(Boolean).join(' · ');

  const respostas = ficha?.respostas ?? [];
  const pesam = respostas.filter(
    (r) => r.respondeu && pesaNaEscolha(r) && valoresDe(r).length > 0,
  );
  const blocos = agruparPorSecao(respostas);

  const sugestoes = useMemo(() => calcularSugestoes(respostas, equipes), [respostas, equipes]);
  const respondeuAlgo = respostas.some((r) => r.respondeu);

  function Resposta({ r }: { r: RespostaFicha }) {
    if (r.tipo === 'foto') {
      if (!r.respondeu) return <p className="text-sm italic text-secondary-300">não respondeu</p>;
      const src = typeof r.resposta === 'string' ? fotos[r.resposta] ?? r.resposta : null;
      if (!src) return <p className="text-sm text-secondary-300">—</p>;
      return (
        <img
          src={src}
          alt={r.pergunta}
          loading="lazy"
          className="mt-1.5 max-h-44 rounded-md object-cover"
        />
      );
    }

    if (r.tipo === 'lista') {
      return <ListaResposta r={r} />;
    }

    if (ehBloco(r)) {
      if (!r.respondeu) {
        return <p className="mt-0.5 text-sm italic text-secondary-300">não respondeu</p>;
      }
      const valores = valoresDe(r);
      if (valores.length === 0) return <p className="mt-0.5 text-sm text-secondary-300">—</p>;
      return (
        <div className="mt-1 space-y-0.5">
          {valores.map((v, i) => (
            <p key={i} className="whitespace-pre-line text-sm text-foreground-800">
              {v}
            </p>
          ))}
        </div>
      );
    }

    // resposta curta, na mesma linha
    return (
      <div className="flex items-baseline justify-between gap-4 py-3">
        <p className="text-sm text-foreground-800">{r.pergunta}</p>
        <p
          className={`shrink-0 text-right text-sm ${
            r.respondeu ? 'font-medium text-foreground-900' : 'italic text-secondary-300'
          }`}
        >
          {r.respondeu ? valoresDe(r).join(', ') : 'não respondeu'}
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="animate-painel-fade absolute inset-0 bg-black/30" onClick={aoFechar} />

      <div className="animate-painel-entrar absolute right-0 top-0 flex h-full w-full flex-col bg-white md:w-[520px]">
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-3">
          <button
            type="button"
            onClick={aoFechar}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900 md:hidden"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            Voltar
          </button>
          <span className="hidden md:block" />
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar ficha"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-secondary-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {estado === 'erro' ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-center">
              <p className="text-sm text-red-700">{erro}</p>
              <button
                type="button"
                onClick={() => setRecarregar((c) => c + 1)}
                className="mt-3 cursor-pointer whitespace-nowrap rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
              >
                Tentar de novo
              </button>
            </div>
          ) : (
            <>
              {/* Cabeçalho */}
              <div className="flex items-start gap-4">
                {fotoUrl ? (
                  <img
                    src={fotoUrl}
                    alt={pessoa.nome}
                    className="h-[120px] w-[120px] shrink-0 rounded-full border-2 border-secondary-200 object-cover"
                  />
                ) : (
                  <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-full bg-secondary-100 text-3xl font-semibold text-secondary-500">
                    {iniciaisDe(pessoa.nome)}
                  </div>
                )}

                <div className="min-w-0 flex-1 pt-1">
                  <p className="text-[15px] font-semibold uppercase leading-tight text-foreground-950">
                    {pessoa.nome}
                  </p>
                  {subtitulo && <p className="mt-0.5 text-sm text-secondary-500">{subtitulo}</p>}
                  <p className="mt-0.5 text-sm text-secondary-600">
                    {ficha?.pessoa?.telefone || 'Sem telefone'}
                  </p>
                  {linhaPapel && (
                    <p className="mt-0.5 text-sm font-medium text-foreground-800">{linhaPapel}</p>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <SeletorAlocar
                  jaEstaEmEquipe={pessoa.equipe_id !== null}
                  equipes={equipes}
                  aoEscolher={(id) => aoEscolherEquipe(pessoa, id)}
                />
                {pessoa.equipe_id !== null && (
                  <button
                    type="button"
                    onClick={() => aoDesalocar(pessoa)}
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-2.5 py-1.5 text-xs font-medium text-secondary-600 transition-colors hover:bg-secondary-100"
                  >
                    <i className="ri-subtract-line text-sm" />
                    tirar da equipe
                  </button>
                )}
              </div>

              {/* Combina com */}
              {estado === 'pronto' &&
                (!respondeuAlgo ? (
                  <p className="mt-4 text-sm text-secondary-500">Ainda não respondeu a ficha.</p>
                ) : sugestoes.length > 0 ? (
                  <div className="mt-4 rounded-lg border border-secondary-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-900">
                        Combina com
                      </h4>
                      <button
                        type="button"
                        onClick={() => setMostrarExplicacao(true)}
                        className="cursor-pointer whitespace-nowrap text-xs text-secondary-500 underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground-900"
                      >
                        o que é isto?
                      </button>
                    </div>

                    <div className="mt-2 space-y-3">
                      {sugestoes.map((s) => (
                        <div key={s.equipeId}>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-bold uppercase tracking-wide text-foreground-900">
                              {s.nome}
                            </p>
                            <span className="shrink-0 whitespace-nowrap text-sm tracking-wider">
                              <span className="text-accent-500">{'★'.repeat(s.estrelas)}</span>
                              <span className="text-secondary-300">{'☆'.repeat(5 - s.estrelas)}</span>
                            </span>
                          </div>
                          <div className="mt-0.5 space-y-0.5">
                            {s.frases.map((frase, i) => (
                              <p key={i} className="text-sm text-secondary-600">
                                &ldquo;{frase}&rdquo;
                              </p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <p className="mt-3 border-t border-secondary-100 pt-2 text-xs text-secondary-500">
                      Não falou nada que aponte para as outras equipes. Isso não quer dizer que não
                      sirva.
                    </p>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-secondary-500">
                    Não falou nada que aponte para uma equipe. Isso não quer dizer que não sirva.
                  </p>
                ))}

              {/* O que pesa na escolha */}
              {pesam.length > 0 && (
                <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50/50 p-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-accent-700">
                    O que pesa na escolha
                  </h4>
                  <div className="mt-1 divide-y divide-accent-100">
                    {pesam.map((r, i) => (
                      <div key={i} className="py-2">
                        <p className="text-xs text-secondary-500">{r.pergunta}</p>
                        <p className="mt-0.5 whitespace-pre-line text-sm text-foreground-900">
                          {valoresDe(r).join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* A ficha completa */}
              {estado === 'carregando' ? (
                <div className="mt-4 space-y-3">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded-lg bg-secondary-100" />
                  ))}
                </div>
              ) : (
                <div className="mt-5">
                  <h4 className="text-sm font-semibold text-foreground-950">A ficha</h4>
                  {blocos.length === 0 ? (
                    <p className="mt-3 text-sm text-secondary-500">
                      Nenhuma resposta na ficha desta pessoa.
                    </p>
                  ) : (
                    <div className="mt-1">
                      {blocos.map((b, bi) => (
                        <div key={bi} className="mt-3">
                          {b.secao && (
                            <p className="border-b border-secondary-100 pb-1 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                              {b.secao}
                            </p>
                          )}
                          <div className="divide-y divide-secondary-100">
                            {b.itens.map((r, ri) => {
                              const bloco = r.tipo === 'foto' || ehBloco(r);
                              return bloco ? (
                                <div key={ri} className="py-3">
                                  <p className="text-sm text-foreground-800">{r.pergunta}</p>
                                  <Resposta r={r} />
                                </div>
                              ) : (
                                <Resposta key={ri} r={r} />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {mostrarExplicacao && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMostrarExplicacao(false)} />
          <div className="relative w-full max-w-sm rounded-lg bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <h5 className="text-sm font-semibold text-foreground-950">O que é isto?</h5>
              <button
                type="button"
                onClick={() => setMostrarExplicacao(false)}
                aria-label="Fechar explicação"
                className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-secondary-500 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              Isto é só uma leitura do que a pessoa escreveu na ficha — o sistema procurou palavras e
              mostrou onde encontrou. Não é avaliação, não é nota, e não sabe nada sobre ela além do
              que ela mesma contou. Quem conhece as pessoas e monta as equipes é você.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}